import React, { useRef, useState } from 'react'
import { shortenSido } from '../utils/address'
import { useNavigate } from 'react-router-dom'
import Avatar from './Avatar'
import PlaceSaveSheet from './PlaceSaveSheet'
import { bookmarkApi } from '../api/bookmark'
import { kakaoApi, KakaoPlace } from '../api/kakao'
import { toastUtil } from '../utils/toast'
import {
  setPlaceSaved,
  usePlaceBookmarkStore,
} from '../stores/placeBookmarkStore'

/** 길게 누르기로 인정하는 시간(ms). 비교 카드와 같은 값으로 맞춘다. */
const HOLD_MS = 500

export interface StoreBoxProps {
  /** 서버 places PK (미저장 검색 결과는 null) */
  id: string | null
  name: string
  address: string
  hasReview?: boolean
  isBookmarked?: boolean
  onRemove?: () => void
  showRemoveButton?: boolean
  removeCategoryId?: string // 북마크 제거 시 필요한 카테고리 ID (toggleBookmark API 필수)
  /** 제거 범위:
   *  - 'category'(기본): removeCategoryId 한 곳에서만 제거 (특정 플레이트 화면)
   *  - 'all': 내가 추가한 모든 리스트에서 완전 해제 (저장한 장소 전체 화면) — 카운트/되살아남 문제 방지 */
  removeScope?: 'category' | 'all'
  /** 카드 영역 클릭 (버튼 제외), 미저장 행은 id가 null */
  onCardClick?: (id: string | null) => void
  /** 미등록(카카오) 결과 — +/북마크 버튼 누를 때 등록(registerKakaoPlace)용 카카오 원본.
   *  있으면 id가 없어도 액션 버튼을 띄우고, 누르는 순간 등록 후 진행한다. */
  kakao?: KakaoPlace
  /** 결과 행에서 액션(+리뷰·북마크)을 실제로 누른 순간 호출.
   *  검색 페이지에서 현재 검색어를 최근 검색어로 저장하는 용도. 미전달 시 no-op. */
  onResultAction?: () => void
  /** (플레이트리스트 전용) 이 장소를 추가한 사람. 넘기면 아이콘 칼럼 맨 앞에 아바타를 표시한다.
   *  저장한 장소 목록에서는 넘기지 않아 아바타가 뜨지 않는다. */
  adderNickname?: string
  adderProfileImage?: string | null
  /** +(리뷰)·북마크 버튼을 감춘다. 카드 탭으로 '고르기'만 하는 목록용 (장소 가져오기 검색 등) */
  hideActionButtons?: boolean
  /**
   * 카드를 길게 누르면 호출. 넘기는 화면에서만 동작한다(플레이트리스트의 '리스트에서 빼기').
   *
   * 탭과 한 칸을 같이 쓰므로 길게 누른 직후의 click 은 삼킨다 — 안 그러면
   * 손을 떼는 순간 자세히보기로 넘어가 팝업이 가려진다.
   */
  onLongPress?: () => void
}

/** 북마크·저장 목록 등 DB 장소 id가 항상 있는 행 */
export type StoreBoxPersistedProps = Omit<StoreBoxProps, 'id'> & { id: string }

const StoreBox: React.FC<StoreBoxProps> = ({
  id,
  name,
  address,
  hasReview = false,
  isBookmarked: initialIsBookmarked = false,
  onRemove,
  showRemoveButton = false,
  removeCategoryId,
  removeScope = 'category',
  onCardClick,
  kakao,
  onResultAction,
  adderNickname,
  adderProfileImage,
  hideActionButtons = false,
  onLongPress,
}) => {
  const navigate = useNavigate()
  const [isAdded, setIsAdded] = useState(false)
  // 등록된 place id. 미등록(카카오) 행은 버튼 누를 때 등록 후 여기에 채워짐.
  const [resolvedId, setResolvedId] = useState<string | null>(id)
  // 저장 여부는 화면 간 공용 상태를 우선한다 — 같은 장소가 피드·지도·검색에
  // 동시에 떠 있어도 아이콘이 따로 놀지 않게. 키는 등록 후 확정되는 resolvedId다.
  const overridden = usePlaceBookmarkStore((state) =>
    resolvedId == null ? undefined : state.saved[String(resolvedId)]
  )
  const isBookmarked = overridden ?? initialIsBookmarked
  const setIsBookmarked = (saved: boolean) => {
    if (resolvedId != null) setPlaceSaved(resolvedId, saved)
  }
  /** 저장 시트를 띄울 장소 id. null이면 닫힘 */
  const [sheetPlaceId, setSheetPlaceId] = useState<number | null>(null)

  /*
   * 길게 누르기. 비교 카드(StoreComparison)에서 검증된 방식을 그대로 쓴다.
   *  - 포인터 이벤트라 마우스·손가락이 같은 코드로 동작한다
   *  - 길게 누른 직후의 click 은 잠시 무시한다(손 떼는 순간 카드가 열리는 것 방지)
   *  - contextmenu 를 막아 안드로이드 길게눌러 뜨는 메뉴가 끼어들지 않게 한다
   */
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heldRef = useRef(false)
  const lastHoldEndRef = useRef(0)

  const clearHoldTimer = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
  }
  const startHold = () => {
    if (!onLongPress) return
    heldRef.current = false
    clearHoldTimer()
    holdTimerRef.current = setTimeout(() => {
      heldRef.current = true
      lastHoldEndRef.current = Date.now()
      onLongPress()
    }, HOLD_MS)
  }
  const endHold = () => {
    clearHoldTimer()
    if (heldRef.current) {
      heldRef.current = false
      lastHoldEndRef.current = Date.now()
    }
  }
  /** 시트를 연 뒤 뒤늦게 저장이 끝나면 올려서 시트 목록을 다시 읽게 한다 */
  const [reloadToken, setReloadToken] = useState(0)

  const hasValidId = (v: string | null) => v != null && v !== '' && v !== '0'
  // id가 있거나, 미등록이라도 등록용 카카오 원본이 있으면 액션 버튼 노출
  const showPlaceActionButtons =
    !hideActionButtons && (hasValidId(resolvedId) || !!kakao)

  // 액션 시점에 place id 확보: 이미 있으면 그대로, 미등록이면 등록(idempotent) 후 반환
  const resolvePlaceId = async (): Promise<string | null> => {
    if (hasValidId(resolvedId)) {
      return resolvedId
    }
    if (kakao) {
      const reg = await kakaoApi.registerKakaoPlace(kakao)
      if (reg?.place_id) {
        const sid = String(reg.place_id)
        setResolvedId(sid)
        return sid
      }
      toastUtil.error('장소 등록에 실패했습니다')
    }
    return null
  }

  const handleAddClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!showPlaceActionButtons) {
      return
    }
    onResultAction?.()
    const pid = await resolvePlaceId()
    if (pid) {
      navigate(`/store-review/${pid}`)
    }
  }

  /**
   * 북마크 탭 — 화면이 어디든 저장 시트를 연다.
   *
   * 아직 저장 전이면 누르는 즉시 저장하고(리스트 미지정 → 기본 저장함) 시트를 연다.
   * 인스타와 같은 흐름이라, 시트에서 어느 플레이트리스트에 넣을지 이어서 고르면 된다.
   * 리스트에서 빼는 것도 이 시트에서 한다(예전 롱프레스 대체).
   */
  const handleBookmarkButtonClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!showPlaceActionButtons) {
      return
    }
    onResultAction?.()
    const pid = await resolvePlaceId()
    if (!pid) return

    const numericId = parseInt(pid, 10)

    // 시트를 먼저 연다. 저장 응답을 기다렸다 열면 탭이 씹힌 것처럼 보인다.
    setSheetPlaceId(numericId)

    if (!isBookmarked) {
      setIsBookmarked(true)
      try {
        await bookmarkApi.toggleBookmark(numericId, {})
        setReloadToken((n) => n + 1)
      } catch (error: any) {
        setIsBookmarked(false)
        toastUtil.error(
          error.response?.data?.message || '저장에 실패했습니다'
        )
      }
    }
  }

  return (
    <>
      <div
        className={`
          w-full
          flex
          items-start
          gap-12px
          py-4px
          [&_img]:select-none
          [&_img]:[-webkit-touch-callout:none]
          ${onCardClick ? 'cursor-pointer' : ''}
          ${onLongPress ? 'select-none' : ''}
        `}
        /*
          touchAction: 'pan-y' — 세로 스크롤은 그대로 두고 나머지 제스처만 막는다.
          select-none 과 함께, 꾹 누르는 동안 글자가 파랗게 선택되는 것을 막는다.
          (팔로워 목록의 UserListItem 길게누르기와 같은 설정)
        */
        style={onLongPress ? { touchAction: 'pan-y' } : undefined}
        onClick={
          onCardClick
            ? () => {
                // 길게 누른 직후(400ms)의 click 은 카드 열기로 보지 않는다
                if (Date.now() - lastHoldEndRef.current < 400) return
                onCardClick(id)
              }
            : undefined
        }
        onPointerDown={onLongPress ? startHold : undefined}
        onPointerUp={onLongPress ? endHold : undefined}
        onPointerLeave={onLongPress ? endHold : undefined}
        onPointerCancel={onLongPress ? endHold : undefined}
        onContextMenu={onLongPress ? (e) => e.preventDefault() : undefined}
        role={onCardClick ? 'button' : undefined}
      >
        {/* 텍스트 칼럼: 이름·주소가 함께 아이콘 왼쪽까지만 차지(기기 폭 무관 자동 말줄임) */}
        <div className="flex-1 min-w-0">
          {/* 매장 이름 (한 줄, 넘치면 …) */}
          <p className="font-pretendard font-700 text-16px leading-125% text-brown-80%-main text-left text-ellipsis-1">
            {name}
          </p>
          {/* 주소 (두 줄, 넘치면 …) */}
          <p className="mt-4px font-pretendard font-regular text-14px leading-20px text-textColorText2 text-left text-ellipsis-2">
            {shortenSido(address)}
          </p>
        </div>

        {showPlaceActionButtons && (
          <div className="flex items-center gap-12px flex-shrink-0">
              {/* (플레이트리스트) 이 장소를 추가한 사람 프로필 — 기본 프로필 80%는 Avatar가 처리 */}
              {adderNickname !== undefined && (
                <Avatar
                  nickname={adderNickname}
                  profileImage={adderProfileImage}
                  size={26}
                />
              )}
              {/* 추가 버튼 (+ 또는 ✓) */}
              {showRemoveButton ? (
                <button
                  type="button"
                  onClick={(e) => handleAddClick(e)}
                  className="flex items-center justify-center"
                  style={{ width: 26, height: 26 }}
                >
                  <img
                    src={
                      isAdded || hasReview
                        ? '/images/checkbox-bold-filled.png'
                        : '/images/bold-plus-icon.png'
                    }
                    alt={isAdded || hasReview ? '추가 완료' : '추가'}
                    draggable={false}
                    className="w-full h-full"
                  />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => handleAddClick(e)}
                  className="flex items-center justify-center"
                  style={{ width: 26, height: 26 }}
                >
                  <img
                    src={
                      hasReview
                        ? '/images/checkbox-bold-filled.png'
                        : '/images/bold-plus-icon.png'
                    }
                    alt={hasReview ? '기록 완료' : '추가'}
                    draggable={false}
                    className="w-full h-full"
                  />
                </button>
              )}

              {/* 북마크 버튼 — 화면과 무관하게 저장 시트를 연다.
                  fill = "내가 저장한 장소인가"(isBookmarked). 리스트에 넣고 빼는 건
                  시트 안에서 리스트별로 한다. */}
              <button
                type="button"
                onClick={(e) => handleBookmarkButtonClick(e)}
                className="flex items-center justify-center"
                style={{ width: 26, height: 26 }}
              >
                <img
                  src={
                    isBookmarked
                      ? '/images/bookmark-filled.png'
                      : '/images/bookmark-empty.png'
                  }
                  alt="북마크"
                  draggable={false}
                  className="w-full h-full"
                />
              </button>
            </div>
          )}
      </div>

      {sheetPlaceId !== null && (
        <PlaceSaveSheet
          isOpen
          onClose={() => setSheetPlaceId(null)}
          placeId={sheetPlaceId}
          placeName={name}
          placeAddress={address}
          reloadToken={reloadToken}
          currentCategoryId={removeCategoryId}
          onRemovedFromCurrentCategory={onRemove}
          onSavedChange={(saved) => {
            setIsBookmarked(saved)
            // 저장을 완전히 해제하면 "저장한 장소"·플레이트리스트 화면에서는
            // 이 행이 더 이상 있을 이유가 없으므로 목록에서 빼달라고 알린다.
            if (!saved) {
              onRemove?.()
            }
          }}
        />
      )}
    </>
  )
}

export default StoreBox
